import { Box } from '@mui/material';
import { type ReactNode } from 'react';
import ToolOptionsDock from '~/components/toolbar/shared/ToolOptionsDock';

interface ToolPropertiesTrayProps {
    /** Names the tool, over its options in the dock. */
    title: string;
    /** The tool's icon, as its rail button shows it, before the title. */
    icon?: ReactNode;
    /** The values in force, shown on the collapsed dock. */
    summary?: ReactNode;
    /**
     * Whether a press on a view collapses the docked options to one line.
     * Off for options the user acts on next, after working on a view.
     */
    collapsesOnViewPress?: boolean;
    /** The `dockAnchor` of the rail button the dock lines up with. */
    anchor?: string;
    /** Rows under the options, such as the modifiers or an Apply. */
    footer?: ReactNode;
    children: ReactNode;
}

/**
 * The options of a toolbar tool in the tool options dock. They flow in
 * rows: compact controls share a row, and a slider or a button that sets
 * its own full width takes a row of its own.
 */
export default function ToolPropertiesTray({
    title,
    icon,
    summary,
    collapsesOnViewPress,
    anchor,
    footer,
    children,
}: ToolPropertiesTrayProps) {
    return (
        <ToolOptionsDock
            kind="tool"
            title={title}
            icon={icon}
            summary={summary}
            collapsesOnViewPress={collapsesOnViewPress}
            anchor={anchor}
            footer={footer}
        >
            <Box
                data-testid="tool-properties-tray"
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    rowGap: 1.5,
                    columnGap: 1,
                }}
            >
                {children}
            </Box>
        </ToolOptionsDock>
    );
}
